import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import UniversityManagerLayout from "../../components/layout/UniversityManagerLayout";
import PermissionGuard from "../../components/common/PermissionGuard";
import UMTransactionRecentActivity from "../../features/university-manager/UMTransactionRecentActivity";
import UMFinanceTransactionTable from "../../features/university-manager/UMFinanceTransactionTable";
import AppButton from "../../components/ui/AppButton";
import { useTenant } from "../../context/TenantContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import useDebounce from "../../utils/useDebounce";
import umService from "../../services/university-admin/umService";

const EMPTY_FILTERS = { userEmail: "", startDate: "", endDate: "", type: "", eventId: "", clubId: "" };

const UMFinanceAnalytics = () => {
  const { t } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { userRole, currentTenantId } = useTenant();
  const { can } = useUniAdminAuth();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [clubs, setClubs] = useState([]);
  const [events, setEvents] = useState([]);

  const debouncedUserEmail = useDebounce(filters.userEmail);

  const fetchLookups = useCallback(async () => {
    const [clubsRes, eventsRes] = await Promise.all([
      can(PERMISSIONS.club.list) ? umService.getAllClubs({ userRole, currentTenantId }) : Promise.resolve(null),
      can(PERMISSIONS.event.list) ? umService.getEvents({ userRole, currentTenantId, page: 0, size: 100 }) : Promise.resolve(null),
    ]);
    if (clubsRes?.success) setClubs(clubsRes.data);
    if (eventsRes?.success) setEvents(eventsRes.data.content);
  }, [userRole, currentTenantId, can]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    const res = await umService.getTransactions({
      userRole,
      currentTenantId,
      page: currentPage,
      size: pageSize,
      userEmail: debouncedUserEmail || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      type: filters.type || undefined,
      eventId: filters.eventId || undefined,
      clubId: filters.clubId || undefined,
    });
    if (res.success) {
      setTransactions(res.data.content);
      setTotalPages(res.data.totalPages);
      setTotalElements(res.data.totalElements);
    } else {
      setTransactions([]);
      setTotalPages(0);
      setTotalElements(0);
    }
    setLoading(false);
  }, [userRole, currentTenantId, currentPage, pageSize, debouncedUserEmail, filters.startDate, filters.endDate, filters.type, filters.eventId, filters.clubId]);

  useEffect(() => { fetchLookups(); }, [fetchLookups]);
  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const handleFilterChange = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setCurrentPage(0);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setCurrentPage(0);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const startPage = Math.max(0, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(<AppButton key={i} variant={i === currentPage ? "primary" : "outline"} className="btn-sm mx-1" onClick={() => setCurrentPage(i)}>{i + 1}</AppButton>);
    }
    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">{tCommon("pagination.showingEntries", { start: currentPage * pageSize + 1, end: Math.min((currentPage + 1) * pageSize, totalElements), total: totalElements })}</div>
        <div className="d-flex align-items-center">
          <AppButton variant="outline" className="btn-sm me-2" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0}>{tCommon("buttons.previous")}</AppButton>
          {pages}
          <AppButton variant="outline" className="btn-sm ms-2" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages - 1}>{tCommon("buttons.next")}</AppButton>
        </div>
      </div>
    );
  };

  return (
    <UniversityManagerLayout>
      <PermissionGuard allow={can(PERMISSIONS.transaction.list)}>
        <div className="finance-analytics-page">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="h3 mb-1 fw-bold text-dark">
                {t("finance.transactions.title")}
              </h1>
              <p className="text-muted mb-0">{t("finance.transactions.subtitle")}</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0">{t("finance.transactions.show")}:</label>
              <select className="form-select form-select-sm" value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setCurrentPage(0); }} style={{ width: "auto" }}>
                <option value={5}>5</option><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
              </select>
            </div>
          </div>

          <UMTransactionRecentActivity />

          <UMFinanceTransactionTable
            transactions={transactions}
            loading={loading}
            totalElements={totalElements}
            clubs={clubs}
            events={events}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />
          {renderPagination()}
        </div>
      </PermissionGuard>
    </UniversityManagerLayout>
  );
};

export default UMFinanceAnalytics;
